import React from 'react'
import PropTypes from 'prop-types'
import Typeahead from './Typeahead'
import TypeaheadSuggestion from './TypeaheadSuggestion'
import { Control, Select, IconWrapper, Field, Button, PrimaryButton } from '../../bulma'
import { LoadingIcon, SearchIcon, ArrowBackIcon } from '../../core/icons'


/**
* Search specific wrapper for the typeahead component.
*/
class SearchTypeahead extends React.Component {
  constructor(props) {
    super(props)

    this.getSectionSuggestions = this.getSectionSuggestions.bind(this)
    this.getSuggestionValue = this.getSuggestionValue.bind(this)
    this.toggleThirdParty = this.toggleThirdParty.bind(this)
    this.suggestionSelectedHandler = this.suggestionSelectedHandler.bind(this)
    this.focusHandler = this.focusHandler.bind(this)
    this.renderSuggestion = this.renderSuggestion.bind(this)
    this.renderInput = this.renderInput.bind(this)

    this.state = {
      isActive: false,
    }
  }

  /**
  * Gets the list of suggestions for the given section.
  *
  * @param section {object}
  * @returns {array} suggestions for the section
  */
  getSectionSuggestions(section) {
    return section.suggestions || []
  }

  /**
  * Gets the value for the given suggestion.
  *
  * @param suggestion {object}
  * @returns {string} formatted value of suggestion
  */
  getSuggestionValue(suggestion) {
    // Include breadcrumbs in suggestion value.
    if (suggestion.crumbs && suggestion.crumbs.length) {
      return `${suggestion.value} in ${suggestion.crumbs.join(' / ')}`
    }

    return suggestion.value
  }

  /**
  * Toggles any obtrusive 3rd party components.
  *
  * @param shouldHide {boolean} If third party components should be hidden.
  */
  toggleThirdParty(shouldHide) {
    // Toggle the chat button.
    if (!window.CustomerSupport) {
      return
    }

    if (shouldHide) {
      window.CustomerSupport.closeChat()
      window.CustomerSupport.hideChatBubble()
    } else {
      window.CustomerSupport.showChatBubble()
    }
  }

  /**
  * Handles when a suggestion has been selected. Tracks the selection in GA and
  * then passes the suggestion on to the `onSubmit` callback.
  *
  * @param e {event}
  * @param suggestion {string} the selected suggestion
  * @param suggestionValue {any} the value of the selected suggestion (equivalent to `getSuggestionValue(suggestion)`)
  * @param suggestionIndex {number} the index of the selected suggestion in the suggestions array
  * @param sectionIndex {number} when rendering multiple sections, this will be the section index (in suggestions) of the selected suggestion. Otherwise, it will be null.
  * @param method {string} describes how user selected the suggestion. The possible values are:
    - 'click': user clicked (or tapped) on the suggestion
    - 'enter': user selected the suggestion using Enter
  */
  suggestionSelectedHandler(e, {
    suggestion, suggestionValue, suggestionIndex, sectionIndex, method,
  }) {
    $.track('event', 'Search Bar Suggestions', suggestion.type, method, suggestionIndex + 1)

    if (typeof this.props.onSubmit === 'function') {
      this.props.onSubmit(suggestionValue, suggestion.slug, method === 'enter')
    }
  }

  /**
  * Handles external changes for focus/blur of the typeahead.
  *
  * @param isFocused {bool} if the input should be focused.
  */
  focusHandler(isFocused) {
    this.toggleThirdParty(isFocused)
    this.setState({ isActive: isFocused })
  }

  /**
  * Renders a given suggestion.
  *
  * @param suggestion {object}
  * @param query {string} Current search query.
  * @returns {TypeaheadSuggestion}
  */
  renderSuggestion(suggestion, { query }) {
    if (!suggestion.type) {
      return false
    }

    return <TypeaheadSuggestion suggestion={suggestion} query={query} />
  }

  /**
  * Overrides the default input render function to include taxon filters and
  * loading icons and such.
  *
  * @param inputProps {object} Props to be applied to the input field.
  */
  renderInput(inputProps) {
    const { taxons, isLoading, isSubmitted } = this.props
    const { isActive } = this.state

    inputProps.className = ['is-medium', inputProps.className || ''].join(' ')

    // Render taxon filter if any present.
    const taxonDropdown = taxons.length ? (
      <Control className="is-hidden-mobile">
        <Select
          name="filters[taxon][]"
          inputSize="small"
          onFocus={() => this.focusHandler(true)}
          onBlur={() => this.focusHandler(false)}
        >
          {taxons.map((taxon, key) => (
            <option value={taxon.taxonId || ''} key={key}>
              {taxon.nested === 'yes' ? '\u00A0\u00A0\u00A0' : false}{taxon.label}
            </option>
          ))}
        </Select>
      </Control>
    ) : false

    // Display a loading icon if there's stuff happening.
    const hasLoadingIcon = isActive && isLoading && !isSubmitted
    const loadingIcon = hasLoadingIcon ? (
      <IconWrapper isRight>
        <LoadingIcon />
      </IconWrapper>
    ) : false

    return (
      <Field hasAddons className="search-typeahead is-marginless">
        {taxonDropdown}
        <Control className="back-button">
          <Button onClick={() => this.focusHandler(false)} className="is-medium">
            <IconWrapper>
              <ArrowBackIcon />
            </IconWrapper>
          </Button>
        </Control>
        <Control hasIconsRight={hasLoadingIcon} isExpanded className="is-visible-mobile">
          <input {...inputProps} />
          {loadingIcon}
        </Control>
        <Control className="search-button">
          <PrimaryButton
            type="submit"
            isLoading={isSubmitted}
            className="is-medium"
          >
            <IconWrapper>
              <SearchIcon />
            </IconWrapper>
          </PrimaryButton>
        </Control>
      </Field>
    )
  }

  render() {
    const {
      value, suggestions, taxons, isLoading, onChange, onSubmit, ...rest
    } = this.props

    // `Autosuggest` has a `shouldRenderSuggestions` function, but it doesn't
    // currently seem to work properly on initial focus, but setting the value
    // directly in `alwaysRenderSuggestions` works as we expect it to.
    const shouldRenderSuggestions = this.state.isActive && !this.props.isSubmitted

    const defaultInputProps = {
      placeholder: 'What can we help you find?',
      type: 'search',
      name: 'keywords',
      value,
      onChange,
      onFocus: () => this.focusHandler(true),
      onBlur: () => this.focusHandler(false),
    }

    return (
      <Typeahead
        multiSection
        suggestions={suggestions}
        onSuggestionSelected={this.suggestionSelectedHandler}
        getSectionSuggestions={this.getSectionSuggestions}
        getSuggestionValue={this.getSuggestionValue}
        renderSuggestion={this.renderSuggestion}
        renderInputComponent={this.renderInput}
        inputProps={defaultInputProps}
        focusInputOnSuggestionClick={false}
        alwaysRenderSuggestions={shouldRenderSuggestions}
        {...rest}
      />
    )
  }
}

SearchTypeahead.defaultProps = {
  suggestions: [],
  value: '',
  taxons: [],
  phrases: ['What can we help you find?'],
}

SearchTypeahead.propTypes = {
  // Suggestions to be sugggested.
  suggestions: PropTypes.array,
  // Current input value.
  value: PropTypes.string,
  // Taxon filter choices.
  taxons: PropTypes.array,
  // Array of possible input placeholders.
  phrases: PropTypes.array,
}

export default SearchTypeahead
